import _ from 'underscore';
import React, { useCallback, useEffect, useContext } from 'react';
import {
  mergeClasses,
  Menu,
  MenuButton,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuTrigger,
  Tooltip,
  Caption1 as Text
} from '@fluentui/react-components';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { withSiteMap, getRoutePathById } from '@bingads-webui-react/with-site-map';

import { getAssetContainerNameWithStatus } from '../utils/get-asset-container-with-status';
import { AssetType, AssetTypeString, MAX_ASSET_CONTAINERS_IN_FILTER } from './const';
import { AssetPerformanceFilterProps } from './types';
import { withAssetGroupsData } from './with-asset-groups';
import { AssetContainer, IScopeContext } from '../utils/types';
import { useFilterStyles } from '../styles/filter-styles';

export function AssetPerformanceFilter({
  i18n,
  isLoading,
  state,
  setState,
  assetContainers,
  onViewAllButtonClick,
  siteMap,
  isSDK,
  logOnClick,
}: AssetPerformanceFilterProps) {
  const styles = useFilterStyles();
  const { scope } = useContext(ScopeContext) as IScopeContext;

  useEffect(() => {
    if (!state && !isLoading && _.isArray(assetContainers)) {
      setState({
        assetType: AssetType.All,
        assetContainer: _.find(assetContainers, container => container.Status === 'Active') || assetContainers[0],
      });
    }
  }, [assetContainers, isLoading, state, setState]);

  const onAssetTypeClick = useCallback((assetType: string) => {
    logOnClick('onAssetTypeClick');
    setState(s => ({
      ...s,
      assetType,
    }));
  }, [setState, logOnClick]);

  const onAssetContainerClick = useCallback((selectedAssetContainer?: AssetContainer) => {
    logOnClick('onAssetContainerClick');
    setState(s => ({
      ...s,
      assetContainer: selectedAssetContainer,
    }));
  }, [setState, logOnClick]);

  const onViewAllAssetGroupsClick = useCallback(() => {
    if (onViewAllButtonClick) {
      onViewAllButtonClick();
    } else {
      scope.save(isSDK ? getRoutePathById(siteMap, 'sdk-assets-settings') : getRoutePathById(siteMap, 'asset-groups'));
    }
  }, [isSDK, onViewAllButtonClick, scope, siteMap]);

  if (!state || _.isEmpty(assetContainers)) {
    return <></>;
  }

  return (
    <div className={mergeClasses('ba-asset-performance-filter', styles.container)}>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Tooltip content={state?.assetContainer?.Name} relationship="description">
            <MenuButton className={mergeClasses('by-asset-container', styles.button, styles.assetContainerFilterButton)} appearance="transparent" disabled={isLoading}>
              <Text className={mergeClasses('asset-filter-button-text', styles.buttonText)}>{state?.assetContainer?.Name}</Text>
            </MenuButton>
          </Tooltip>
        </MenuTrigger>
        <MenuPopover className="asset-containers">
          <MenuList>
            {_.map(assetContainers, (assetGroup: AssetContainer) => (
              <MenuItem className={mergeClasses('asset-container', styles.assetContainer)} onClick={() => onAssetContainerClick(assetGroup)} key={assetGroup.Id}>{getAssetContainerNameWithStatus(assetGroup, styles)}</MenuItem>
            ))}
            {assetContainers.length > MAX_ASSET_CONTAINERS_IN_FILTER && <MenuItem className={mergeClasses('view-all', styles.viewAll)} onClick={onViewAllAssetGroupsClick}>{i18n.getString(_TL_('View all asset groups'))}</MenuItem>}
          </MenuList>
        </MenuPopover>
      </Menu>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Tooltip content={i18n.getString(AssetTypeString[state?.assetType])} relationship="description">
            <MenuButton className={mergeClasses('by-asset-type', styles.button)} appearance="transparent" disabled={isLoading}>
              <Text className={mergeClasses('type-filter-button-text', styles.buttonText)}>{i18n.getString(AssetTypeString[state?.assetType])}</Text>
            </MenuButton>
          </Tooltip>
        </MenuTrigger>
        <MenuPopover className="asset-types">
          <MenuList>
            {
              _.values(AssetType).map((assetType: string) =>
                <MenuItem className={assetType} onClick={() => onAssetTypeClick(assetType)} key={assetType}>{i18n.getString(AssetTypeString[assetType])}</MenuItem>)
            }
          </MenuList>
        </MenuPopover>
      </Menu>
    </div>
  );
}

AssetPerformanceFilter.defaultProps = {
  state: undefined,
};

export const AssetPerformanceFilterWithAssetGroupsData = withSiteMap(withAssetGroupsData(AssetPerformanceFilter));
